import {
  NameStepsEnumCustomerV2,
  OrgTypeEnum,
  NamesOfFieldEnum,
  ClientId,
  SharedTypeRequestFieldsCustomerV2,
  AccountDetailsFormFieldsCustomerV2,
  CompanyDetailsFormFieldsCustomerV2,
  PayformTechDetailsFormFieldsCustomerV2,
  FormDataCustomerV2,
  AdditionalDetailsFormFieldsCustomerV2,
} from "~/typings/types";
import { getValuesFromObject } from "~/utils/getValuesFromObject";
import {
  postDraftCompanyDetails,
  postDraftAccountDetails,
  postDraftPayformTechDetails,
  postDraftAdditionalDetails,
  ReqPostArguments,
  ResPost
} from "./apiMethods";
import { cleanText, getFieldNameWithCondition, sanitizeData } from "~/utils";
import { defaultAxiosRequestConfig } from "~/api/apiMethods";
import { SanitizationRules, sanitizeDataObject } from "./sanitizeDataObject";

type RequestFunction = ({
  data,
  config
}: ReqPostArguments<SharedTypeRequestFieldsCustomerV2>) => Promise<ResPost>;
type RequestConfig = Omit<Record<NameStepsEnumCustomerV2, RequestFunction>, NameStepsEnumCustomerV2.AttachmentsDetails>;

const requestConfig: RequestConfig = {
  [NameStepsEnumCustomerV2.CompanyDetails]: postDraftCompanyDetails,
  [NameStepsEnumCustomerV2.AccountDetails]: postDraftAccountDetails,
  [NameStepsEnumCustomerV2.PayformTechDetails]: postDraftPayformTechDetails,
  [NameStepsEnumCustomerV2.AdditionalDetails]: postDraftAdditionalDetails
};

interface ApiService {
  values: FormDataCustomerV2;
  pageId: Exclude<NameStepsEnumCustomerV2, NameStepsEnumCustomerV2.AttachmentsDetails>;
  clientId: ClientId;
  orgType: OrgTypeEnum;
  logoFileId?: any;
  customSanitizationRules?: SanitizationRules;
}

export const apiServiceCustomer = async ({
  values,
  pageId,
  clientId,
  orgType,
  logoFileId,
  customSanitizationRules
}: ApiService) =>
  await new Promise((resolve, reject) => {
    let reqData: any;

    if (pageId === NameStepsEnumCustomerV2.AccountDetails) {
      reqData = {
        id: cleanText(clientId),
        ui_version: "v1",
        fields: sanitizeDataObject({
          [NamesOfFieldEnum.OrgType]: orgType,
          [NamesOfFieldEnum.CrmPhone]: values?.[NamesOfFieldEnum.CrmPhone] ?? null,
          [NamesOfFieldEnum.LprPhone]: values?.[NamesOfFieldEnum.LprPhone] ?? null,
          [NamesOfFieldEnum.BuhPhone]: values?.[NamesOfFieldEnum.BuhPhone] ?? null,
          [NamesOfFieldEnum.TechPhone]: values?.[NamesOfFieldEnum.TechPhone] ?? null,
          [NamesOfFieldEnum.CrmBirthday]: values?.[NamesOfFieldEnum.CrmBirthday],
          [NamesOfFieldEnum.LrpProxyDocDate]: values?.[NamesOfFieldEnum.LrpProxyDocDate],
          [NamesOfFieldEnum.PostalAddress]: values?.[NamesOfFieldEnum.PostalAddressRadio] === 'yes' ? values?.[NamesOfFieldEnum.LegalAddress] : values?.[NamesOfFieldEnum.PostalAddress],
          ...getValuesFromObject<
            Omit<
              AccountDetailsFormFieldsCustomerV2,
              | NamesOfFieldEnum.OrgType
              | NamesOfFieldEnum.CrmPhone
              | NamesOfFieldEnum.LprPhone
              | NamesOfFieldEnum.BuhPhone
              | NamesOfFieldEnum.TechPhone
              | NamesOfFieldEnum.CrmBirthday
              | NamesOfFieldEnum.LrpProxyDocDate
              | NamesOfFieldEnum.PostalAddress
            >
          >({
            object: values,
            values: [
              NamesOfFieldEnum.CrmNameFirst,
              NamesOfFieldEnum.CrmNameLast,
              NamesOfFieldEnum.CrmNamePatron,
              NamesOfFieldEnum.CrmEmail,
              NamesOfFieldEnum.Inn,
              NamesOfFieldEnum.LprName,
              NamesOfFieldEnum.BuhName,
              NamesOfFieldEnum.TechName,
              NamesOfFieldEnum.LrpProxyDocNumber,
              NamesOfFieldEnum.LegalAddress,
              getFieldNameWithCondition(orgType === OrgTypeEnum.Company, NamesOfFieldEnum.Name),
              getFieldNameWithCondition(orgType === OrgTypeEnum.Company, NamesOfFieldEnum.Ogrn),
              getFieldNameWithCondition(orgType === OrgTypeEnum.IndividualEntrepreneur, NamesOfFieldEnum.Ogrnip),
            ]
          })
        }, customSanitizationRules)
      };
    }

    if (pageId === NameStepsEnumCustomerV2.CompanyDetails) {
      reqData = {
        id: cleanText(clientId),
        ui_version: "v1",
        fields: sanitizeDataObject({
          [NamesOfFieldEnum.OrgType]: orgType,
          [NamesOfFieldEnum.VatMode]: orgType === OrgTypeEnum.SelfEmployed ? 'npd' : values?.[NamesOfFieldEnum.VatMode],
          [NamesOfFieldEnum.Vat]: values?.[NamesOfFieldEnum.IsVat] === 'no' ? 'no' : values?.[NamesOfFieldEnum.Vat],
          ...getValuesFromObject<Omit<CompanyDetailsFormFieldsCustomerV2, NamesOfFieldEnum.OrgType | NamesOfFieldEnum.VatMode | NamesOfFieldEnum.Vat>>({
            object: values,
            values: [
              NamesOfFieldEnum.BankName,
              NamesOfFieldEnum.SettlementAccount,
              NamesOfFieldEnum.CorrespondentAccount,
              NamesOfFieldEnum.BankBic,
            ]
          })
        }, customSanitizationRules)
      };
    }

    if (pageId === NameStepsEnumCustomerV2.PayformTechDetails) {
      const rawFields = {
        [NamesOfFieldEnum.PayformLinkTg]: values?.[NamesOfFieldEnum.PayformLinkTg],
        [NamesOfFieldEnum.PayformLinkOther]: values?.[NamesOfFieldEnum.PayformLinkOther],
        [NamesOfFieldEnum.PayformLinkSite]: values?.[NamesOfFieldEnum.PayformLinkSite],
        [NamesOfFieldEnum.PayformLinkInstagram]: values?.[NamesOfFieldEnum.PayformLinkInstagram],
        [NamesOfFieldEnum.PayformLinkVk]: values?.[NamesOfFieldEnum.PayformLinkVk],
        [NamesOfFieldEnum.PayformName]: `https://${values?.payform_name}.payform.ru`,
      };

      const sanitized = sanitizeDataObject(
        {
          [NamesOfFieldEnum.OrgType]: orgType,
          [NamesOfFieldEnum.LogoMainFileId]: logoFileId?.length ? logoFileId[0] : null,
          [NamesOfFieldEnum.PayformPhone]: values?.[NamesOfFieldEnum.PayformPhone],
          ...(orgType === OrgTypeEnum.SelfEmployed && values?.[NamesOfFieldEnum.IsNeedRecurrentPayments] === 'no' ? { 'payform_type': 'pf2' } : { 'payform_type': 'pf1' }),
          ...getValuesFromObject<
            Omit<
              PayformTechDetailsFormFieldsCustomerV2,
              NamesOfFieldEnum.OrgType
              | NamesOfFieldEnum.PayformName
              | NamesOfFieldEnum.PayformPhone
              | NamesOfFieldEnum.LogoMainFileId
            >
          >({
            object: values,
            values: [
              NamesOfFieldEnum.ProductType,
              NamesOfFieldEnum.ProductName,
              NamesOfFieldEnum.ProductDescription,
              NamesOfFieldEnum.PayformEmail,
              NamesOfFieldEnum.PayformPaymentMethods,
              NamesOfFieldEnum.IsHaveEducationalLicense,
              NamesOfFieldEnum.IsNeedOnlineKassa,
              getFieldNameWithCondition(orgType === OrgTypeEnum.SelfEmployed, NamesOfFieldEnum.IsNeedExternalIntegrations),
              getFieldNameWithCondition(orgType === OrgTypeEnum.SelfEmployed, NamesOfFieldEnum.IsNeedRecurrentPayments),
            ],
          }),
        },
        customSanitizationRules
      );

      reqData = {
        id: cleanText(clientId),
        ui_version: "v1",
        fields: {
          ...sanitized,
          ...rawFields,
        },
      };
    }


    if (pageId === NameStepsEnumCustomerV2.AdditionalDetails) {
      reqData = {
        id: cleanText(clientId),
        ui_version: "v1",
        fields: sanitizeDataObject({
          [NamesOfFieldEnum.OrgType]: orgType,
          ...getValuesFromObject<Omit<AdditionalDetailsFormFieldsCustomerV2, NamesOfFieldEnum.OrgType>>({
            object: values,
            values: [
              NamesOfFieldEnum.ClientFrom,
              NamesOfFieldEnum.ClientFromOther,
              NamesOfFieldEnum.ClientPromocode,
              NamesOfFieldEnum.PaymentMethodProdamusSubscription,
            ]
          })
        }, customSanitizationRules)
      };
    }

    requestConfig?.[pageId]({ data: reqData, config: defaultAxiosRequestConfig })
      .then(res => resolve(res))
      .catch(err => {
        reject(err);
      });
  });